import React, { FC, ReactNode, useCallback, useContext, useEffect } from 'react'

import { Modal } from '@afterpaytouch/core'
import { OrderResponse } from '@afterpaytouch/portal-api'

import { ClaimEligibility } from './Steps/ClaimEligibility/ClaimEligibility'
import { Section75DisputeModalContext, Section75DisputeModalProvider, Section75DisputeModalSteps } from './Section75DisputeModalContext'
import { CreateFailure, CreateSuccess } from './Steps/Result'
import { ChooseItems } from './Steps/ChooseItems'
import { NotEligible } from './Steps/NotEligible'
import { DisputeDetails } from './Steps/DisputeDetails'
import { DisputeAmount } from './Steps/DisputeAmount'
import { OtherDocs } from './Steps/OtherDocs'
import { Review } from './Steps/Review'

export interface Section75DisputeModalProps {
  isModalOpen: boolean
  closeModal: () => void
  order: OrderResponse
  refreshDisputeInfo: () => void
  animate: boolean
  reopenGetHelpModal: () => void
  openNormalDisputeModal?: () => void
}

export const Section75DisputeSteps: FC<Omit<Section75DisputeModalProps, 'order' | 'refreshDisputeInfo'>> = ({
  isModalOpen,
  closeModal,
  animate,
  reopenGetHelpModal,
  openNormalDisputeModal,
}) => {
  const {
    step,
    isLoading,
    result: { success, error },
    resetState,
  } = useContext(Section75DisputeModalContext)

  const renderStep: () => ReactNode = useCallback(() => {
    if (success) {
      return <CreateSuccess />
    }

    if (error) {
      return <CreateFailure />
    }
    switch (step) {
      case Section75DisputeModalSteps.ClaimEligibility:
        return <ClaimEligibility />
      case Section75DisputeModalSteps.ChooseItems:
        return <ChooseItems />
      case Section75DisputeModalSteps.NotEligible:
        return <NotEligible openNormalDisputeModal={openNormalDisputeModal} />
      case Section75DisputeModalSteps.DisputeDetails:
        return <DisputeDetails />
      case Section75DisputeModalSteps.DisputeAmount:
        return <DisputeAmount />
      case Section75DisputeModalSteps.OtherDocs:
        return <OtherDocs />
      case Section75DisputeModalSteps.Review:
        return <Review />
    }
  }, [success, error, step, reopenGetHelpModal])

  useEffect(() => {
    return () => {
      resetState()
    }
  }, [])

  return (
    <Modal
      show={isModalOpen}
      onClose={closeModal}
      onBackdropClick={closeModal}
      onHide={closeModal}
      onEscapeKeyDown={closeModal}
      testNameSpace='section75-dispute-modal'
      loading={isLoading}
      animate={animate}
    >
      {renderStep()}
    </Modal>
  )
}

export const Section75DisputeModal: FC<Section75DisputeModalProps> = ({
  isModalOpen,
  closeModal,
  order,
  refreshDisputeInfo,
  animate,
  reopenGetHelpModal,
  openNormalDisputeModal,
}) => {
  return isModalOpen ? (
    <Section75DisputeModalProvider closeModal={closeModal} order={order} refreshDisputeInfo={refreshDisputeInfo}>
      <Section75DisputeSteps
        isModalOpen={isModalOpen}
        closeModal={closeModal}
        animate={animate}
        reopenGetHelpModal={reopenGetHelpModal}
        openNormalDisputeModal={openNormalDisputeModal}
      />
    </Section75DisputeModalProvider>
  ) : null
}
